import { MESSAGE_TYPES } from 'hooks/websocket/constants'
import type { WebSocketData } from 'hooks/websocket/types'
import { buildTranslate } from 'locales'
import {
  createWebSocketError,
  WebSocketErrorCodes,
  WebSocketErrorHandler,
} from 'services/websocket/errorHandling'
import { calloutHandler } from 'services/websocket/messageHandlers/calloutHandler'
import { pickerHandler } from 'services/websocket/messageHandlers/pickerHandler'
import type {
  MessageHandler,
  MessageHandlerContext,
  MessageHandlerRegistry,
} from 'services/websocket/messageHandlers/types'
import {
  selectCurrentStreamingMessage,
  setAwaitingResponse,
  setChatId,
  setCurrentStreamingMessage,
  setIsLoading,
  updateMessages,
} from 'slices/aiConversations'
import type { Message } from 'slices/aiConversations/types'
import { setSegmentSaving } from 'slices/aiSegmentBuilder'
import * as uuid from 'uuid'

const t = buildTranslate('components.ai.segment_visualization')

const clearToolCalls = (context: MessageHandlerContext) => {
  const currentStreamingMessage = selectCurrentStreamingMessage(
    context.getState()
  )
  if (currentStreamingMessage?.isToolCall) {
    context.dispatch(setCurrentStreamingMessage(null))
  }
}

export const processMessage = (
  message: WebSocketData,
  context: MessageHandlerContext
): void => {
  if (message.chat_id) {
    context.dispatch(setChatId(message.chat_id))
    if (context.onChatIdReceived) {
      context.onChatIdReceived(message.chat_id)
    }
  }

  const isContentMessage = [
    MESSAGE_TYPES.AI_RESPONSE,
    MESSAGE_TYPES.TEXT_CHUNK,
    MESSAGE_TYPES.TOOL_CALL,
    MESSAGE_TYPES.SEGMENT_DATA,
    MESSAGE_TYPES.PICKER,
    MESSAGE_TYPES.ERROR,
    MESSAGE_TYPES.MESSAGE_COMPLETE,
  ].includes(message.type as any)

  if (isContentMessage) {
    context.dispatch(setAwaitingResponse(false))
  }

  const handler = messageHandlers[message.type as keyof typeof messageHandlers]

  if (!handler) {
    // eslint-disable-next-line no-console
    console.warn(`[MessageProcessor] Unknown message type: ${message.type}`)
    return
  }

  handler.handle(message, context)
}

const connectionEstablishedHandler: MessageHandler = {
  type: MESSAGE_TYPES.CONNECTION_ESTABLISHED,
  handle: (_message, _context) => {
    // No-op
  },
}

const authErrorHandler: MessageHandler = {
  type: MESSAGE_TYPES.AUTH_ERROR,
  handle: (message, context) => {
    context.dispatch(setSegmentSaving(false))
    context.setConnectionError(t('auth_error'))
    context.setIsConnected(false)
  },
}

const aiResponseHandler: MessageHandler = {
  type: MESSAGE_TYPES.AI_RESPONSE,
  handle: (message, context) => {
    clearToolCalls(context)
    context.dispatch(setSegmentSaving(false))

    const aiMessage: Message = {
      id: uuid.v4(),
      content: message.content || '',
      type: 'ai',
      timestamp: new Date(),
      isMarkdown: message.markdown || message.isMarkdown || false,
      sequenceNumber:
        message.sequenceNumber ?? ++context.sequenceCounter.current,
      serverId: message.serverId,
      clientId: uuid.v4(),
      isOptimistic: false,
    }
    context.addMessageWithOrdering(aiMessage)
    context.dispatch(setIsLoading(false))
  },
}

const textChunkHandler: MessageHandler = {
  type: MESSAGE_TYPES.TEXT_CHUNK,
  handle: (message, context) => {
    const currentStreamingMessage = selectCurrentStreamingMessage(
      context.getState()
    )

    if (!currentStreamingMessage) {
      context.removeToolCallMessages()
    }

    if (currentStreamingMessage?.isToolCall) {
      context.removeToolCallMessages()
      const newMessage: Message = {
        id: uuid.v4(),
        content: message.content || '',
        type: 'ai',
        timestamp: new Date(),
        isMarkdown: message.markdown || message.isMarkdown || false,
        sequenceNumber:
          message.sequenceNumber ?? ++context.sequenceCounter.current,
        serverId: message.serverId,
        clientId: uuid.v4(),
        isOptimistic: false,
      }
      context.dispatch(setCurrentStreamingMessage(newMessage))
    } else if (currentStreamingMessage) {
      const updatedMessage: Message = {
        ...currentStreamingMessage,
        content: currentStreamingMessage.content + (message.content || ''),
      }
      context.dispatch(setCurrentStreamingMessage(updatedMessage))
    } else {
      const newMessage: Message = {
        id: uuid.v4(),
        content: message.content || '',
        type: 'ai',
        timestamp: new Date(),
        isMarkdown: message.markdown || message.isMarkdown || false,
        sequenceNumber:
          message.sequenceNumber ?? ++context.sequenceCounter.current,
        serverId: message.serverId,
        clientId: uuid.v4(),
        isOptimistic: false,
      }
      context.dispatch(setCurrentStreamingMessage(newMessage))
    }

    context.dispatch(setIsLoading(true))
  },
}

const messageCompleteHandler: MessageHandler = {
  type: MESSAGE_TYPES.MESSAGE_COMPLETE,
  handle: (message, context) => {
    context.clearStreamingTimeout()
    context.dispatch(setSegmentSaving(false))

    const currentStreamingMessage = selectCurrentStreamingMessage(
      context.getState()
    )

    const shouldClearLoading = !!currentStreamingMessage

    if (currentStreamingMessage) {
      context.addMessageWithOrdering(currentStreamingMessage)
    }

    context.removeToolCallMessages()

    context.dispatch(setCurrentStreamingMessage(null))

    // This prevents premature clearing from empty message_complete signals
    if (shouldClearLoading) {
      context.dispatch(setIsLoading(false))
    }
  },
}

const toolCallHandler: MessageHandler = {
  type: MESSAGE_TYPES.TOOL_CALL,
  handle: (message, context) => {
    const toolCallMessage: Message = {
      id: uuid.v4(),
      content: message.content || '',
      type: 'tool_call',
      timestamp: new Date(),
      isMarkdown: false,
      sequenceNumber:
        message.sequenceNumber ?? ++context.sequenceCounter.current,
      serverId: message.serverId,
      clientId: uuid.v4(),
      isOptimistic: false,
      isToolCall: true,
    }

    const currentStreamingMessage = selectCurrentStreamingMessage(
      context.getState()
    )
    if (
      currentStreamingMessage &&
      currentStreamingMessage.type === 'ai' &&
      !currentStreamingMessage.isToolCall &&
      currentStreamingMessage.content
    ) {
      context.addMessageWithOrdering(currentStreamingMessage)
    }

    context.dispatch(setCurrentStreamingMessage(toolCallMessage))
    context.dispatch(setIsLoading(true))
  },
}

const segmentDataHandler: MessageHandler = {
  type: MESSAGE_TYPES.SEGMENT_DATA,
  handle: (message, context) => {
    context.clearStreamingTimeout()
    context.dispatch(setSegmentSaving(false))
    clearToolCalls(context)

    try {
      if (!message.content) {
        throw new Error('No content to parse')
      }
      const segmentJson = JSON.parse(message.content)

      let dslObject
      try {
        dslObject =
          typeof segmentJson.dsl === 'string'
            ? JSON.parse(segmentJson.dsl)
            : segmentJson.dsl
      } catch {
        dslObject = null
      }

      const groups = Array.isArray(segmentJson.groups) ? segmentJson.groups : []

      const validStates = ['draft', 'requires_approval', 'visible'] as const
      const rawState = segmentJson.segment_state
      const segmentState = validStates.includes(rawState as any)
        ? rawState
        : undefined

      const savedSegmentId = segmentJson.segment_id || segmentJson.id

      const segmentVisualization = {
        title: segmentJson.segment_name || t('untitled_segment'),
        description: segmentJson.description || t('no_description'),
        estimatedCount: segmentJson.estimated_count || 0,
        content: {
          filters: {
            groups,
          },
        },
        rawDsl: dslObject,
        segmentState,
        savedSegmentId,
      }

      // Get current messages from state
      const state = context.getState()
      const currentMessages =
        state.aiConversations?.activeConversation?.messages || []

      // Find the most recent message with draft segment visualization to update
      const messageToUpdate = [...currentMessages]
        .reverse() // Check most recent first
        .find(
          msg =>
            msg.segmentVisualizations &&
            msg.segmentVisualizations.length > 0 &&
            msg.segmentVisualizations.some(viz => viz.segmentState === 'draft')
        )

      if (
        messageToUpdate &&
        segmentState &&
        (segmentState === 'requires_approval' || segmentState === 'visible')
      ) {
        // Update the existing message's segment visualization in-place
        const updatedMessages = currentMessages.map(msg => {
          if (msg.id === messageToUpdate.id) {
            return {
              ...msg,
              segmentVisualizations:
                msg.segmentVisualizations?.map(viz =>
                  viz.segmentState === 'draft' ? segmentVisualization : viz
                ) || [],
            }
          }
          return msg
        })

        context.dispatch(updateMessages(updatedMessages))
      } else {
        // Fallback: create new message if no draft segment found or state is still draft
        const confirmationMessage: Message = {
          id: uuid.v4(),
          content: '',
          type: 'ai',
          timestamp: new Date(),
          isMarkdown: false,
          sequenceNumber:
            message.sequenceNumber ?? ++context.sequenceCounter.current,
          serverId: message.serverId,
          clientId: uuid.v4(),
          isOptimistic: false,
          segmentVisualizations: [segmentVisualization],
        }

        context.addMessageWithOrdering(confirmationMessage)
      }

      context.dispatch(setIsLoading(false))
    } catch (error) {
      const errorMessage: Message = {
        id: uuid.v4(),
        content: t('parse_error'),
        type: 'ai',
        timestamp: new Date(),
        isMarkdown: false,
        sequenceNumber:
          message.sequenceNumber ?? ++context.sequenceCounter.current,
        serverId: message.serverId,
        clientId: uuid.v4(),
        isOptimistic: false,
      }
      context.addMessageWithOrdering(errorMessage)
      context.dispatch(setIsLoading(false))
    }
  },
}

const suggestionChipsHandler: MessageHandler = {
  type: MESSAGE_TYPES.SUGGESTION_CHIPS,
  handle: (message, context) => {
    context.dispatch(setSegmentSaving(false))

    try {
      if (!message.content) {
        // eslint-disable-next-line no-console
        console.error('[StreamProcessor] SUGGESTION_CHIPS has no content')
        return
      }
      const suggestions = JSON.parse(message.content)

      if (!Array.isArray(suggestions)) {
        // eslint-disable-next-line no-console
        console.error(
          '[StreamProcessor] SUGGESTION_CHIPS content is not an array'
        )
        return
      }

      const suggestionMessage: Message = {
        id: uuid.v4(),
        content: '',
        type: 'ai',
        timestamp: new Date(),
        isMarkdown: false,
        sequenceNumber:
          message.sequenceNumber ?? ++context.sequenceCounter.current,
        serverId: message.serverId,
        clientId: uuid.v4(),
        isOptimistic: false,
        suggestions,
      }

      context.addMessageWithOrdering(suggestionMessage)
      // Clear loading state since suggestion chips are non-streaming messages
      context.dispatch(setIsLoading(false))
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(
        '[StreamProcessor] Failed to parse SUGGESTION_CHIPS:',
        error
      )
      // Clear loading state even on error
      context.dispatch(setIsLoading(false))
    }
  },
}

const errorHandler: MessageHandler = {
  type: MESSAGE_TYPES.ERROR,
  handle: (message, context) => {
    context.clearStreamingTimeout()
    context.dispatch(setSegmentSaving(false))
    context.removeToolCallMessages()

    const errorCode =
      typeof message.code === 'string'
        ? message.code
        : WebSocketErrorCodes.UNKNOWN_MESSAGE_TYPE

    const errorContent =
      typeof message.content === 'string'
        ? message.content
        : 'Unknown error occurred'

    const wsError = createWebSocketError('message', errorCode, errorContent, {
      recoverable: false,
    })

    // Only use locale-translated strings when the server explicitly sent a code
    // that matches a known frontend WS_* code. Otherwise, show the server's
    // content directly. This handles:
    // - No code (forwarded gRPC errors from thanx-ai) → show server content
    // - Backend codes (message_too_large, rate_limit_exceeded) → show server content
    // - Known WS_* codes → locale-translated strings
    const hasExplicitKnownCode =
      typeof message.code === 'string' &&
      Object.values(WebSocketErrorCodes).includes(errorCode as any)

    const userMessage = hasExplicitKnownCode
      ? WebSocketErrorHandler.getMessageForUser(wsError)
      : errorContent

    const errorMessage: Message = {
      id: uuid.v4(),
      content: userMessage,
      type: 'ai',
      timestamp: new Date(),
      isMarkdown: false,
      sequenceNumber:
        message.sequenceNumber ?? ++context.sequenceCounter.current,
      serverId: message.serverId,
      clientId: uuid.v4(),
      isOptimistic: false,
    }
    context.addMessageWithOrdering(errorMessage)
    context.dispatch(setCurrentStreamingMessage(null))
    context.dispatch(setIsLoading(false))
  },
}

const messageHandlers: MessageHandlerRegistry = {
  [MESSAGE_TYPES.CONNECTION_ESTABLISHED]: connectionEstablishedHandler,
  [MESSAGE_TYPES.AUTH_ERROR]: authErrorHandler,
  [MESSAGE_TYPES.AI_RESPONSE]: aiResponseHandler,
  [MESSAGE_TYPES.TEXT_CHUNK]: textChunkHandler,
  [MESSAGE_TYPES.MESSAGE_COMPLETE]: messageCompleteHandler,
  [MESSAGE_TYPES.TOOL_CALL]: toolCallHandler,
  [MESSAGE_TYPES.SEGMENT_DATA]: segmentDataHandler,
  [MESSAGE_TYPES.SUGGESTION_CHIPS]: suggestionChipsHandler,
  [MESSAGE_TYPES.PICKER]: pickerHandler,
  [MESSAGE_TYPES.CALLOUT]: calloutHandler,
  [MESSAGE_TYPES.ERROR]: errorHandler,
} as const

export { messageHandlers }
export type {
  MessageHandler,
  MessageHandlerContext,
} from 'services/websocket/messageHandlers/types'
